import { apiGet, apiPostPut } from "./api_methods";
import { RECRUITER_SUBSCRIPTIONS_BASE } from "./endpoints";

/**
 * Get available plans for the logged-in recruiter (GET /v1/recruiter/subscriptions/plans)
 * @param {Object} [params] - Optional query params (e.g. { duration: "month" | "year" })
 * @returns {Promise<{ body, status }>}
 */
export async function getPlans(params = {}) {
  const query = new URLSearchParams(params).toString();
  const path = query
    ? `${RECRUITER_SUBSCRIPTIONS_BASE}/plans?${query}`
    : `${RECRUITER_SUBSCRIPTIONS_BASE}/plans`;
  return apiGet(path);
}

/**
 * Get the current active subscription for the logged-in recruiter (GET /v1/recruiter/subscriptions/my-subscription)
 * @returns {Promise<{ body, status }>}
 */
export async function getMySubscription() {
  return apiGet(`${RECRUITER_SUBSCRIPTIONS_BASE}/my-subscription`);
}

/**
 * Get payment/transaction history (GET /v1/recruiter/subscriptions/payment-transactions)
 * @param {Object} [params] - Optional query params { skip, limit }
 * @returns {Promise<{ body, status }>}
 */
export async function getPaymentTransactions(params = {}) {
  const q = {};
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;

  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${RECRUITER_SUBSCRIPTIONS_BASE}/payment-transactions?${query}`
    : `${RECRUITER_SUBSCRIPTIONS_BASE}/payment-transactions`;
  return apiGet(path);
}

/**
 * Subscribe to a plan (POST /v1/recruiter/subscriptions/subscribe)
 * For free plans, activates immediately. For paid plans, returns status 0 indicating payment is needed.
 * @param {string} planId - The plan ID to subscribe to
 * @returns {Promise<{ body, status }>}
 */
export async function subscribe(planId) {
  return apiPostPut(
    { planId },
    `${RECRUITER_SUBSCRIPTIONS_BASE}/subscribe`,
    "POST",
  );
}

/**
 * Create a payment order for a subscription (POST /v1/recruiter/subscriptions/create-order)
 * Returns checkoutPageUrl for PhonePe gateway.
 * @param {string} subscriptionId - The subscription ID
 * @param {string} [couponCode] - Optional coupon code
 * @returns {Promise<{ body, status }>}
 */
export async function createOrder(subscriptionId, couponCode) {
  const body = { subscriptionId };
  if (couponCode) body.couponCode = couponCode;
  return apiPostPut(
    body,
    `${RECRUITER_SUBSCRIPTIONS_BASE}/create-order`,
    "POST",
  );
}

/**
 * Verify payment after PhonePe redirect (GET /v1/public/subscriptions/verify-payment/:orderId)
 * Public endpoint — no auth required.
 * @param {string} orderId - The order ID to verify
 * @returns {Promise<{ body, status }>}
 */
export async function verifyPayment(orderId) {
  return apiGet(`/v1/public/subscriptions/verify-payment/${orderId}`);
}

/**
 * Consume a feature usage (POST /v1/recruiter/subscriptions/consume-feature)
 * Decrements the limit for the given feature key.
 * @param {string} featureKey - e.g. "AI Resume Analyser"
 * @returns {Promise<{ body, status }>}
 */
export async function consumeFeature(featureKey) {
  return apiPostPut(
    { featureKey },
    `${RECRUITER_SUBSCRIPTIONS_BASE}/consume-feature`,
    "POST",
  );
}

/**
 * Buy extra feature limits a la carte (POST /v1/recruiter/subscriptions/buy-features)
 * Returns a PhonePe order.
 * @param {Array<{name: string, count: number}>} features - Features to buy
 * @returns {Promise<{ body, status }>}
 */
export async function buyFeatures(features) {
  return apiPostPut(
    { features },
    `${RECRUITER_SUBSCRIPTIONS_BASE}/buy-features`,
    "POST",
  );
}

/**
 * Verify payment for bought features (GET /v1/recruiter/subscriptions/verify-features/:orderId)
 * @param {string} orderId - The order ID to verify
 * @returns {Promise<{ body, status }>}
 */
export async function verifyFeatures(orderId) {
  return apiGet(`${RECRUITER_SUBSCRIPTIONS_BASE}/verify-features/${orderId}`);
}

/**
 * Check a promo/coupon code (POST /v1/recruiter/subscriptions/check-promo-code)
 * @param {string} code - The promo code
 * @param {number} price - The current price to check against
 * @returns {Promise<{ body, status }>}
 */
export async function checkPromoCode(code, price) {
  return apiPostPut(
    { code, price },
    `${RECRUITER_SUBSCRIPTIONS_BASE}/check-promo-code`,
    "POST",
  );
}

/**
 * Redeem a promo/coupon code (POST /v1/recruiter/subscriptions/redeem-promo-code)
 * @param {string} code - The promo code to redeem
 * @returns {Promise<{ body, status }>}
 */
export async function redeemPromoCode(code) {
  return apiPostPut(
    { code },
    `${RECRUITER_SUBSCRIPTIONS_BASE}/redeem-promo-code`,
    "POST",
  );
}
